import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queries/query-keys";

export interface SiteContent {
  [key: string]: string;
}

// Fetch all site content
export function useSiteContent() {
  return useQuery({
    queryKey: queryKeys.siteContent.all,
    queryFn: async (): Promise<SiteContent> => {
      const response = await fetch("/api/content");
      if (!response.ok) {
        throw new Error("Failed to fetch site content");
      }
      const data = await response.json();
      return data.content;
    },
  });
}

// Fetch single content by key
export function useSiteContentByKey(key: string) {
  return useQuery({
    queryKey: queryKeys.siteContent.byKey(key),
    queryFn: async (): Promise<string | null> => {
      const response = await fetch(`/api/content/${key}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error("Failed to fetch content");
      }
      const data = await response.json();
      return data.content.value;
    },
  });
}

// Update single content
export function useUpdateSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const response = await fetch(`/api/admin/content/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) {
        throw new Error("Failed to update content");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.siteContent.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.siteContent.byKey(variables.key) });
    },
  });
}

// Bulk update content
export function useBulkUpdateSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: Record<string, string>) => {
      const response = await fetch("/api/admin/content", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) {
        throw new Error("Failed to update content");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.siteContent.all });
    },
  });
}
